/**
 * LoginModal - Authentication Required Modal
 * Shows a message and redirects users to the Auth page
 * Replaces the old authentication modal with a simple redirect approach
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  autoRedirect?: boolean;
  redirectDelay?: number;
}

export default function LoginModal({ 
  isOpen, 
  onClose,
  message = 'You need to login first to use this feature',
  autoRedirect = false,
  redirectDelay = 5
}: LoginModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(redirectDelay);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(redirectDelay);
    }
  }, [isOpen, redirectDelay]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !autoRedirect) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      handleRedirect();
    }
  }, [isOpen, countdown, autoRedirect]);

  const handleRedirect = () => {
    const returnUrl = location.pathname + location.search;
    navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    onClose();
  };

  const handleRegisterRedirect = () => {
    const returnUrl = location.pathname + location.search;
    navigate(`/register?returnUrl=${encodeURIComponent(returnUrl)}`);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Authentication Required">
      <div className="space-y-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-primary-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        {/* Message */}
        <div className="text-center">
          <p className="text-lg text-gray-900 mb-2">
            {message}
          </p>
          <p className="text-sm text-gray-600">
            Please sign in or create an account to continue
          </p>
        </div>

        {/* Auto-redirect countdown */}
        {autoRedirect && countdown > 0 && (
          <div className="text-center">
            <p className="text-sm text-gray-500">
              Redirecting to login page in <span className="font-semibold text-primary-600">{countdown}</span> seconds...
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3">
          <Button
            onClick={handleRedirect}
            variant="primary"
            size="lg"
            className="w-full"
          >
            Sign In
          </Button>

          <Button
            onClick={handleRegisterRedirect}
            variant="outline"
            size="lg"
            className="w-full"
          >
            Create Account
          </Button>

          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
          >
            Maybe Later
          </button>
        </div>

        {/* Additional Info */}
        <div className="text-center pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </Modal>
  );
}
